import React, { useMemo } from 'react';
import { useStore } from 'effector-react';
import hexRgb from 'hex-rgb';
import rgbHex from 'rgb-hex';
import type { HSIconImage, HSIconSeqFrame, HSIconVideo } from '@types';
import ColorPicker from '@components/ColorPicker';
import colorStringToObj from '@utils/colorStringToObj';
import Button from '@components/Button';
import Slider from '@components/Slider';
import {
  $editingHotspot,
  $editingHotspotApi,
} from '@stores/editingHotspotStores';
import { MainPlayer } from '@stores/krpPlayerStores';
import WidgetRow from '@components/PlayerWidgets/WidgetRow';
import KrpColorPicker from './KrpColorPicker';

type Icon = HSIconImage | HSIconSeqFrame | HSIconVideo;

const toChromakey = (params: {
  colorString: string;
  threshold: number;
  smoothing: number;
}) => {
  const { r, g, b } = colorStringToObj(params.colorString);
  return `0x${rgbHex(r, g, b)}|${params.threshold}|${params.smoothing}`;
};

const STEP = 0.01;
const valToString = (num: number) => String(Math.round(num * 100) / 100);

function ChromakeyTab() {
  const { hotspot, isBatch, batchHotspots } = useStore($editingHotspot)!;
  const { chromakey } = hotspot.icon as Icon;

  const {
    threshold = 0.3,
    smoothing = 0.1,
    colorString,
  } = useMemo(() => {
    try {
      const [hex, thresholdStr, smoothingStr] = chromakey!.split('|');
      const [r, g, b] = hexRgb(hex.replace('0x', ''), { format: 'array' });
      return {
        colorString: `${r},${g},${b},1`,
        threshold: Number(thresholdStr),
        smoothing: Number(smoothingStr),
      };
    } catch (e) {
      return {};
    }
  }, [chromakey]);

  const updateIcon = (val: string) => {
    if (!isBatch) {
      MainPlayer.get()?.updateHotspot({ id: hotspot.id, chromakey: val });
    } else {
      (batchHotspots ?? []).forEach((item) => {
        MainPlayer.get()?.updateHotspot({ id: item.id, chromakey: val });
      });
    }

    $editingHotspotApi.update((draft) => {
      (draft.icon as Icon).chromakey = val;
    });
  };

  const handleColorStringChange = (str: string) => {
    const val = toChromakey({ colorString: str, smoothing, threshold });
    updateIcon(val);
  };

  const handleThresholdChange = (num: number) => {
    const val = toChromakey({
      colorString: colorString!,
      smoothing,
      threshold: num,
    });
    updateIcon(val);
  };

  const handleSmoothingChange = (num: number) => {
    const val = toChromakey({
      colorString: colorString!,
      smoothing: num,
      threshold,
    });
    updateIcon(val);
  };

  const handleClear = () => {
    updateIcon('');
  };

  return (
    <>
      <WidgetRow>
        <ColorPicker
          colorString={colorString}
          onChange={handleColorStringChange}
        >
          <ColorPicker.Header>选取要去除的颜色：</ColorPicker.Header>
          <div className="flex items-center">
            <ColorPicker.Bar
              className="flex-1"
              disableAlpha
              placeholder="点击选择颜色"
            />
            <KrpColorPicker
              className="ml-10"
              onChange={handleColorStringChange}
            />
          </div>
        </ColorPicker>
      </WidgetRow>
      {!!colorString && (
        <WidgetRow>
          <Slider
            min={0}
            max={1}
            value={threshold}
            valToString={valToString}
            onChange={handleThresholdChange}
          >
            <Slider.Header title="蒙板透明度：" />
            <Slider.Bar step={STEP} />
            <Slider.Control step={STEP} />
          </Slider>
          <Slider
            className="mt-20"
            min={0}
            max={1}
            value={smoothing}
            valToString={valToString}
            onChange={handleSmoothingChange}
          >
            <Slider.Header title="平滑度：" />
            <Slider.Bar step={STEP} />
            <Slider.Control step={STEP} />
          </Slider>
        </WidgetRow>
      )}
      {!!colorString && (
        <Button className="mb-10 w-full" onClick={handleClear}>
          清除设置
        </Button>
      )}
    </>
  );
}

export default ChromakeyTab;
